import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { CuttingService, IResponseRule, IRule } from '@shared/services/cutting.service';
import { ExcelFileTransfer, xlsxImportError } from '@shared/services/file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '@shared/services/file-transfer-services/interfaces';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, forkJoin, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Rules';
const importConcurrency = 5;

export interface ICuttingRuleExportData {
	name: string;
	disableOptimization?: boolean;
	useCoatingInOptimization?: boolean;
	// 0 is the highest priority, so it must be distinguished from "not set" - null/undefined, not 0.
	priority?: number;
	description?: string;
	batchType?: string;
	material: string;
	profile?: string;
	machine: string;
	setupName: string;
	segregation?: string;
	logisticsFormula?: string;
}

// Schemas are module-level consts and cannot call TranslateService at declaration time, so the
// literal prefix is duplicated here rather than reused from the class's own translationPrefix.
const templateHelpPrefix = 'masterdata.cutting.ruleImportExport.import.template-help';

const CuttingRuleSchema: IFileDataSchema<ICuttingRuleExportData> = {
	name: sheetName,
	properties: {
		name: {
			type: 'string',
			required: true,
			fileKey: 'Name',
			comment: `${templateHelpPrefix}.name`,
			example: 'Example Rule',
			width: 20,
		},
		disableOptimization: {
			type: 'boolean',
			required: false,
			fileKey: 'DisableOptimization',
			comment: `${templateHelpPrefix}.disableOptimization`,
			example: false,
			width: 22,
		},
		useCoatingInOptimization: {
			type: 'boolean',
			required: false,
			fileKey: 'UseCoatingInOptimization',
			comment: `${templateHelpPrefix}.useCoatingInOptimization`,
			example: false,
			width: 28,
		},
		priority: {
			type: 'number',
			required: false,
			fileKey: 'Priority',
			comment: `${templateHelpPrefix}.priority`,
			example: 1,
			width: 15,
		},
		description: {
			type: 'string',
			required: false,
			fileKey: 'Description',
			comment: `${templateHelpPrefix}.description`,
			example: 'Example description',
			width: 30,
		},
		batchType: {
			type: 'string',
			required: false,
			fileKey: 'BatchType',
			comment: `${templateHelpPrefix}.batchType`,
			example: 'Example batch type',
			width: 20,
		},
		material: {
			type: 'string',
			required: true,
			fileKey: 'Material',
			comment: `${templateHelpPrefix}.material`,
			example: 'Example material',
			width: 20,
		},
		profile: {
			type: 'string',
			required: false,
			fileKey: 'Profile',
			comment: `${templateHelpPrefix}.profile`,
			example: 'Example profile',
			width: 20,
		},
		machine: {
			type: 'string',
			required: true,
			fileKey: 'Machine',
			comment: `${templateHelpPrefix}.machine`,
			example: 'Example machine',
			width: 20,
		},
		setupName: {
			type: 'string',
			required: true,
			fileKey: 'SetupName',
			comment: `${templateHelpPrefix}.setupName`,
			example: 'Example Setup',
			width: 20,
		},
		segregation: {
			type: 'string',
			required: false,
			fileKey: 'Segregation',
			comment: `${templateHelpPrefix}.segregation`,
			example: 'Example segregation',
			width: 20,
		},
		logisticsFormula: {
			type: 'string',
			required: false,
			fileKey: 'LogisticsFormula',
			comment: `${templateHelpPrefix}.logisticsFormula`,
			example: '(KRMSAML AND KRMFARVE) OR RAMSAML',
			width: 40,
		},
	},
};

interface CuttingRuleImportContext {
	ruleNameMap: Map<string, IResponseRule>;
	setupSelfByName: Map<string, string>;
}

/** A row that passed validation, carrying its file location through to a save failure too. */
interface CuttingRuleImportCandidate {
	rule: IRule;
	location?: string;
}

@Injectable({
	providedIn: 'root',
})
export class CuttingRuleExcelService implements ITransferService<IResponseRule> {
	private translationPrefix = 'masterdata.cutting.ruleImportExport';

	constructor(
		private cuttingService: CuttingService,
		private excelFileTransfer: ExcelFileTransfer<ICuttingRuleExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.excelFileTransfer.generateTemplate(`${sheetName}Template`, CuttingRuleSchema);
	};

	// Rule names are not unique on the backend, so name-based matching is only safe once we have
	// actively confirmed there is no collision - both among the existing rules and within the file
	// being exported/imported. Nothing here treats name as unique on faith.
	public exportData(rules: IResponseRule[]): Observable<IResponseRule[]> {
		const duplicateNames = findDuplicateNames(rules);
		if (duplicateNames.length > 0) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, { names: duplicateNames.join(', ') })));
		}

		return this.cuttingService.getSetups().pipe(
			map((setups) => this.buildSetupNameBySelfMap(setups.items)),
			map((setupNameBySelf) => rules.map((rule) => this.toExportRow(rule, setupNameBySelf))),
			mergeMap((data) => this.excelFileTransfer.exportData(sheetName, data, CuttingRuleSchema)),
			map(() => rules),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	private toExportRow(rule: IResponseRule, setupNameBySelf: Map<string, string>): ICuttingRuleExportData {
		return {
			name: rule.name,
			disableOptimization: rule.disableOptimization ?? false,
			useCoatingInOptimization: rule.useCoatingInOptimization ?? false,
			// ?? rather than ||: 0 is the highest priority, so it must not be coalesced away too.
			priority: rule.priority ?? undefined,
			description: rule.description || '',
			batchType: rule.batchType?.self ? this.cuttingService.getLastSegment(rule.batchType.self) : '',
			material: rule.material?.self ? this.cuttingService.getLastSegment(rule.material.self) : '',
			profile: rule.profile?.self ? this.cuttingService.getLastSegment(rule.profile.self) : '',
			machine: rule.machine?.self ? this.cuttingService.getLastSegment(rule.machine.self) : '',
			setupName: this.resolveSetupNameForExport(rule, setupNameBySelf),
			segregation: rule.segregation?.identifier || '',
			logisticsFormula: rule.logisticsFormula ?? '',
		};
	}

	private resolveSetupNameForExport(rule: IResponseRule, setupNameBySelf: Map<string, string>): string {
		const setupName = setupNameBySelf.get(rule.setup?.self);
		if (!setupName) {
			throw new Error(
				this.translator.instant(`${this.translationPrefix}.export.errors.missing-setup`, {
					name: rule.name,
				}),
			);
		}
		return setupName;
	}

	// Duplicate names (file or roster), per-row validation problems (missing name, unknown setup),
	// and per-row save failures all get discovered and reported together in one pass, instead of a
	// user fixing one category, re-importing, and only then discovering the next. Validating a row
	// only needs the setup lookup, not unambiguous rule-name matching, so it's safe to run even when
	// names collide - only the actual create/update is withheld then, since it's the one step that
	// depends on matching a row to exactly one existing rule by name.
	public importData(file: File | File[], _overwrite: boolean = false): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed and a real parse failure is already collected - stop here instead
				// of still hitting the roster, whose own failure would otherwise mask the actual
				// workbook parse error the user needs to see.
				if (parsedRows.length === 0 && ImportErrors.length > 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return forkJoin({
					rules: this.cuttingService.getRules(),
					setups: this.cuttingService.getSetups(),
				}).pipe(
					mergeMap(({ rules, setups }) => {
						const rosterDuplicates = findDuplicateNames(rules.items);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						const ctx: CuttingRuleImportContext = {
							ruleNameMap: buildRuleNameMap(rules.items),
							setupSelfByName: this.buildSetupSelfByNameMap(setups.items),
						};

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ctx, ImportErrors)),
							toArray(),
							mergeMap((validRules) => (namesAreAmbiguous ? of([]) : from(validRules).pipe(mergeMap(this.createOrUpdateRule(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<ICuttingRuleExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, CuttingRuleSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Name is what the rest of this service matches rows against, so a blank-after-trim name is
	// rejected here rather than silently normalized away by findDuplicateNames/normalizeName.
	private validateRow(ctx: CuttingRuleImportContext, ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<ICuttingRuleExportData>): CuttingRuleImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			if (!normalizeName(row.name)) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-required-fields`),
				};
			}

			const setupSelf = ctx.setupSelfByName.get(normalizeName(row.setupName));
			if (!setupSelf) {
				errors['setupName'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.setup-name-not-found`, { name: row.setupName }),
				};
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: row.name,
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			const rule: IRule = {
				self: null,
				name: row.name,
				disableOptimization: row.disableOptimization ?? false,
				useCoatingInOptimization: row.useCoatingInOptimization ?? false,
				// ?? rather than ||: 0 is the highest priority, so it must not be coalesced away too.
				priority: row.priority ?? null,
				description: row.description || '',
				batchType: row.batchType || null,
				material: row.material,
				profile: row.profile || null,
				machine: row.machine,
				setup: setupSelf,
				segregation: row.segregation || null,
				optimization: null,
				updatedDate: null,
				updatedBy: null,
				logisticsFormula: row.logisticsFormula || null,
			};

			return { rule, location };
		};
	}

	private createOrUpdateRule(ctx: CuttingRuleImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: CuttingRuleImportCandidate) => {
			const { rule, location } = candidate;
			// Safe: importData() has already confirmed neither the file nor the roster has a name
			// collision, so at most one existing rule can match here.
			const existingRule = ctx.ruleNameMap.get(normalizeName(rule.name));

			if (existingRule) {
				rule.self = existingRule.self;
				// optimization has no Excel column, so update preserves whatever the existing rule
				// already has - logisticsFormula does have a column and is file-driven instead.
				rule.optimization = existingRule.optimization?.identifier ?? null;
				return this.cuttingService.updateRule(rule).pipe(
					map(() => ({ entity: rule, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, rule, location, error);
						return of();
					}),
				);
			}

			return this.cuttingService.createRule(rule).pipe(
				map(() => ({ entity: rule, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, rule, location, error);
					return of();
				}),
			);
		};
	}

	private saveFailedErrorKey(error: unknown): string {
		switch ((error as HttpErrorResponse)?.status) {
			case 400:
				// Only the API validates the formula, so a refused formula is named rather than reported as invalid data.
				return (error as HttpErrorResponse).error?.errors?.logisticsFormula ? 'save-failed-invalid-logistics-formula' : 'save-failed-bad-request';
			case 404:
				return 'save-failed-not-found';
			default:
				return 'save-failed';
		}
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], rule: IRule, location: string | undefined, error: unknown) {
		ImportErrors.push({
			Id: rule.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${this.saveFailedErrorKey(error)}`, { name: rule.name }),
		});
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}

	private buildSetupNameBySelfMap(setups: { self: string; name: string }[]): Map<string, string> {
		const map = new Map<string, string>();
		setups.forEach((setup) => map.set(setup.self, setup.name));
		return map;
	}

	private buildSetupSelfByNameMap(setups: { self: string; name: string }[]): Map<string, string> {
		const map = new Map<string, string>();
		setups.forEach((setup) => map.set(normalizeName(setup.name), setup.self));
		return map;
	}
}

function buildRuleNameMap(rules: IResponseRule[]): Map<string, IResponseRule> {
	const map = new Map<string, IResponseRule>();
	rules.forEach((rule) => map.set(normalizeName(rule.name), rule));
	return map;
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
